import { useState } from 'react';
import {
  PhoneIcon,
  XMarkIcon,
  ChevronUpIcon,
  SignalIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/solid';

import { useSip } from '@/contexts/SipContext';
import { Dialpad } from './Dialpad';
import { CallControls } from './CallControls';
import { CallTimer } from './CallTimer';
import { IncomingCallDialog } from './IncomingCallDialog';

const CONNECTION_LABELS: Record<string, string> = {
  disconnected: 'Odpojeno',
  connecting: 'Připojování…',
  connected: 'Připojeno',
  registered: 'Registrováno',
};

const CONNECTION_COLORS: Record<string, string> = {
  disconnected: 'text-zinc-400',
  connecting: 'text-amber-500',
  connected: 'text-emerald-500',
  registered: 'text-emerald-500',
};

export function SoftphonePanel() {
  const sip = useSip();
  const [isOpen, setIsOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [showDialpad, setShowDialpad] = useState(true);

  // Don't show anything if SIP is not configured
  if (!sip.isConfigured) return null;

  const isInCall = sip.callState !== 'idle';

  // ── Incoming call overlay ──
  if (sip.callState === 'ringing_in' && sip.callInfo) {
    return (
      <>
        <IncomingCallDialog
          remoteNumber={sip.callInfo.remoteNumber}
          onAnswer={sip.answer}
          onReject={sip.hangup}
        />
        {/* Keep the minimized button visible too */}
        <MinimizedButton
          connectionState={sip.connectionState}
          isInCall={isInCall}
          callDuration={sip.callDuration}
          onClick={() => setIsOpen(true)}
        />
      </>
    );
  }

  // ── Minimized state ──
  if (!isOpen) {
    return (
      <MinimizedButton
        connectionState={sip.connectionState}
        isInCall={isInCall}
        callDuration={sip.callDuration}
        onClick={() => setIsOpen(true)}
      />
    );
  }

  // ── Expanded panel ──
  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[320px]
                 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl
                 border border-zinc-200 dark:border-zinc-800
                 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <PhoneIcon className="w-4 h-4 text-zinc-500" />
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Telefon
          </span>
          <span className={`text-xs ${CONNECTION_COLORS[sip.connectionState]}`}>
            {CONNECTION_LABELS[sip.connectionState]}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <XMarkIcon className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-4">
        {/* Active call info */}
        {isInCall && sip.callInfo && (
          <div className="text-center py-2">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">
              {sip.callState === 'ringing_out' ? 'Vyzvánění…' : 'Probíhá hovor'}
            </p>
            <p className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              {sip.callInfo.remoteNumber}
            </p>
            {sip.callState === 'in_call' && (
              <div className="flex items-center justify-center gap-2 mt-1">
                <CallTimer seconds={sip.callDuration} />
                {sip.isRecording && (
                  <span className="flex items-center gap-1 text-xs text-red-500">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    REC
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Dial input */}
        {!isInCall && (
          <div className="relative">
            <input
              type="tel"
              value={dialNumber}
              onChange={(e) => setDialNumber(e.target.value)}
              placeholder="Zadejte číslo…"
              className="w-full text-center text-xl font-mono tracking-wider
                         py-3 px-4 rounded-xl
                         bg-zinc-50 dark:bg-zinc-800
                         border border-zinc-200 dark:border-zinc-700
                         text-zinc-900 dark:text-zinc-100
                         placeholder:text-zinc-300 dark:placeholder:text-zinc-600
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dialNumber.trim()) {
                  sip.makeCall(dialNumber.trim());
                }
              }}
            />
            {dialNumber && (
              <button
                type="button"
                onClick={() => setDialNumber('')}
                className="absolute right-3 top-1/2 -translate-y-1/2
                           text-zinc-400 hover:text-zinc-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Dialpad toggle */}
        {!isInCall && (
          <button
            type="button"
            onClick={() => setShowDialpad(!showDialpad)}
            className="flex items-center justify-center gap-1 text-xs text-zinc-400 hover:text-zinc-600"
          >
            <ChevronUpIcon
              className={`w-3 h-3 transition-transform ${showDialpad ? 'rotate-180' : ''}`}
            />
            {showDialpad ? 'Skrýt číselník' : 'Zobrazit číselník'}
          </button>
        )}

        {/* Dialpad */}
        {showDialpad && !isInCall && (
          <Dialpad
            onDigit={(digit) => {
              setDialNumber((prev) => prev + digit);
              sip.sendDtmf(digit);
            }}
          />
        )}

        {/* In-call dialpad for DTMF */}
        {isInCall && sip.callState === 'in_call' && showDialpad && (
          <Dialpad onDigit={(digit) => sip.sendDtmf(digit)} />
        )}

        {/* Call controls */}
        <CallControls
          callState={sip.callState}
          isMuted={sip.isMuted}
          onCall={() => {
            if (dialNumber.trim()) sip.makeCall(dialNumber.trim());
          }}
          onHangup={sip.hangup}
          onAnswer={sip.answer}
          onToggleMute={sip.toggleMute}
          onToggleHold={sip.toggleHold}
        />
      </div>
    </div>
  );
}

// ── Minimized floating button ──

function MinimizedButton({
  connectionState,
  isInCall,
  callDuration,
  onClick,
}: {
  connectionState: string;
  isInCall: boolean;
  callDuration: number;
  onClick: () => void;
}) {
  const isConnected = connectionState === 'registered' || connectionState === 'connected';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2
                  px-4 py-3 rounded-full shadow-lg
                  transition-all hover:scale-105
                  ${
                    isInCall
                      ? 'bg-emerald-500 text-white shadow-emerald-500/25'
                      : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-800'
                  }`}
    >
      {isConnected ? (
        <SignalIcon className="w-4 h-4" />
      ) : (
        <SignalSlashIcon className="w-4 h-4 text-zinc-400" />
      )}
      {isInCall ? (
        <CallTimer seconds={callDuration} />
      ) : (
        <PhoneIcon className="w-4 h-4" />
      )}
    </button>
  );
}
